import { FormikProps, useFormikContext } from 'formik'
import {
  ChannelPartnerPrivateOfferInCreation,
  CppoPriceAdjustmentType,
} from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { BoxV2, PlanType } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { StandardCustomDimensions } from '../../../../common/components/StandardCustomDimensions/StandardCustomDimensions'
import { get } from 'lodash'
import { getMappedMonthKey } from '../ContractDuration/ContractDuration'
import { isNonEmptyNumber } from '@labrav/common'
import { useStyles } from './ProductDimensions.styles'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import { ContractDurationType, DiscountType } from '../../../../types/flyout'
import { getMarkupValues } from '../../../../utils/channelOfferUtil'
import { getPricesArrayForDimensions } from '../helpers'

export const ProductDimensions = ({
  resellerAuthorization,
}: {
  productType?: PlanType
  resellerAuthorization?: ReceivedResellerAuthorization
}) => {
  const { classes } = useStyles()
  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { values, setFieldValue } = formikProps

  const dimensions = (values?.dimensions || []).filter(
    dimensionItem => !dimensionItem?.isAdditionalUsageAllowed
  )

  const isCustomDuration =
    resellerAuthorization?.contractDurationType ===
      ContractDurationType.CUSTOM ||
    resellerAuthorization?.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS

  const prices = getPricesArrayForDimensions({
    channelOffer: values,
    resellerAuthorization,
  })

  const handleChangeQuantity = async (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    await setFieldValue(`dimensions.[${index}].quantity`, event?.target?.value)

    const selectedValue = values?.adjustmentPercentageInputValue
    if (values?.flexiblePaymentTerms && isNonEmptyNumber(selectedValue)) {
      const installmentsData = values?.installments || []

      if (
        values?.priceAdjustmentType === CppoPriceAdjustmentType.PERCENTAGE &&
        values?.dimensions
      ) {
        const updatedDimensions = values?.dimensions
        updatedDimensions[index].quantity =
          event?.target?.value === '' ? 0 : Number(event?.target?.value)

        const { markupOrTotal } = getMarkupValues({
          dimensions: updatedDimensions,
          adjustmentPercentage: values?.adjustmentPercentageInputValue,
          priceAdjustmentType: values?.priceAdjustmentType,
          resaleAuthorization: resellerAuthorization,
        })
        const updatedInstallments = installmentsData.map(item => ({
          ...item,
          amount: parseFloat(
            (Number(markupOrTotal!) / installmentsData.length).toFixed(2)
          ),
        }))
        await setFieldValue('installments', updatedInstallments)
      }
    }
  }

  return (
    <BoxV2
      heading={'Product dimensions'}
      headingIcon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '18px', height: '14px' }}
        />
      }
    >
      <div className={classes.root}>
        {prices?.map(priceItem => {
          const priceItemMonth = isCustomDuration
            ? resellerAuthorization?.contractDurationInMonthsForEndCustomer || 0
            : getMappedMonthKey(priceItem)
          const priceCommonLable = `${priceItemMonth}-month${
            priceItemMonth > 1 ? 's' : ''
          }`
          return (
            <div className={classes.rowWrapper} data-testid={'dimension-table'}>
              {resellerAuthorization?.discountType ===
              DiscountType.FLEXIBLE_PAYMENT_TERMS ? (
                <></>
              ) : (
                <div className={classes.tableSubHeading}>
                  {!values?.flexiblePaymentTerms
                    ? `Pricing per dimension for ${priceCommonLable} contract`
                    : 'Specify the quantity of each dimension that buyers can use within the contract duration. If you wish to exclude any dimension from the offer, enter 0.'}
                </div>
              )}
              <StandardCustomDimensions
                formikProps={formikProps}
                dimensions={dimensions?.map(item => {
                  const resaleDimension = (
                    resellerAuthorization?.dimensions || []
                  )?.find(
                    resaleDimension =>
                      resaleDimension?.name === item?.name &&
                      !resaleDimension?.isAdditionalUsageAllowed
                  )
                  const wholesalePrice = get(
                    isCustomDuration
                      ? resaleDimension
                      : resaleDimension?.prices,
                    priceItem
                  )
                  const buyerPrice = get(
                    isCustomDuration ? item : item.prices,
                    priceItem
                  )
                  return {
                    ...item,
                    wholesalePrice,
                    buyerPrice,
                    margin: buyerPrice ? buyerPrice - wholesalePrice : '',
                  }
                })}
                priceColumnName={!isCustomDuration ? 'prices' : ''}
                priceArray={[]}
                customColumns={[
                  ...(values?.flexiblePaymentTerms ||
                  resellerAuthorization?.discountType ===
                    DiscountType.FLEXIBLE_PAYMENT_TERMS
                    ? [
                        ...(resellerAuthorization?.discountType !==
                        DiscountType.FLEXIBLE_PAYMENT_TERMS
                          ? [
                              {
                                keyName: 'wholesalePrice',
                                lable: `Wholesale ${priceCommonLable} price`,
                              },
                            ]
                          : []),
                        {
                          keyName: 'quantity',
                          lable: `${priceCommonLable} quantity`,
                        },
                      ]
                    : [
                        {
                          keyName: 'wholesalePrice',
                          lable: `Wholesale ${priceCommonLable} price`,
                        },

                        {
                          keyName: `${
                            !isCustomDuration ? 'prices.' : ''
                          }${priceItem}`,
                          lable: `Buyer ${priceCommonLable} price`,
                        },

                        {
                          keyName: 'margin',
                          lable: 'Margin',
                        },
                      ]),
                ]}
                handleChangeQuantity={handleChangeQuantity}
                hideDesc={!values?.flexiblePaymentTerms}
                isEditableQuantity={
                  resellerAuthorization?.discountType ===
                  DiscountType.FLEXIBLE_PAYMENT_TERMS
                    ? false
                    : values?.flexiblePaymentTerms
                }
              />
            </div>
          )
        })}
      </div>
    </BoxV2>
  )
}

export { getPricesArrayForDimensions } from '../helpers'
